import InteractiveAnnotation from "./interactiveAnnotation";
import { drawEllipse, setLineStyle, setBoxStyle, drawBox, boxSize } from "../canvasUtils";
import { getArcPosition, isInBoundsBox, isInBoundsLine } from "../geometry";
import Ellipse from "./ellipse";

export default class ETDRSGrid extends InteractiveAnnotation {
  constructor(position, options) {
    super(position, options);

    this.centerX = position.x;
    this.centerY = position.y;

    this.color = options.color || "#2ECE2E";

    this.circles = [null, null, null];

    this.lines = [
      {
        direction: "se",
        angle: Math.PI / 4,
        inner: null,
        outer: null
      },
      {
        direction: "ne",
        angle: -Math.PI / 4,
        inner: null,
        outer: null
      },
      {
        direction: "s",
        angle: Math.PI / 2,
        inner: null,
        outer: null
      },
      {
        direction: "n",
        angle: -Math.PI / 2,
        inner: null,
        outer: null
      },
      {
        direction: "sw",
        angle: 3 * Math.PI / 4,
        inner: null,
        outer: null
      },
      {
        direction: "nw",
        angle: -3 * Math.PI / 4,
        inner: null,
        outer: null
      },
      {
        direction: "e",
        angle: 0,
        inner: null,
        outer: null
      },
      {
        direction: "w",
        angle: Math.PI,
        inner: null,
        outer: null
      }
    ];
    this.canvasSize = null;
    this.width = null;
    this.height = null;
    this.radius = null;
    this.circleRadius = 3 / this.scale;

    this.activeBox = false;
    this.initialMousePos = null;
    this.sizeSnapshop = null;
    this.circleSnapshop = null;
    this.isActive = false;
  }

  get topLeft() {
    return { x: this.centerX - this.radius, y: this.centerY - this.radius, side: "TL", location: "O"};
  }

  get topRight() {
    return { x: this.centerX + this.radius, y: this.centerY - this.radius, side: "TR", location: "O"};
  }

  get bottomLeft() {
    return { x: this.centerX - this.radius, y: this.centerY + this.radius, side: "BL", location: "O"};
  }

  get bottomRight() {
    return { x: this.centerX + this.radius, y: this.centerY + this.radius, side: "BR", location: "O"};
  }

  get innerTopLeft() {
    return { x: this.centerX - this.radius / 2.75, y: this.centerY - this.radius / 2.75, side: "TL", location: "I"};
  }

  get innerTopRight() {
    return { x: this.centerX + this.radius / 2.75, y: this.centerY - this.radius / 2.75, side: "TR", location: "I"};
  }

  get innerBottomLeft() {
    return { x: this.centerX - this.radius / 2.75, y: this.centerY + this.radius / 2.75, side: "BL", location: "I"};
  }

  get innerBottomRight() {
    return { x: this.centerX + this.radius / 2.75, y: this.centerY + this.radius / 2.75, side: "BR", location: "I"};
  }

  getBoxes() {
    return [
      this.topLeft,
      this.topRight,
      this.bottomLeft, 
      this.bottomRight,
      this.innerTopLeft,
      this.innerTopRight,
      this.innerBottomLeft, 
      this.innerBottomRight
    ];
  }

  draw(ctx) {
    if(!this.canvasSize) return;

    let circleRadiusArray = [this.circleRadius / 6, this.circleRadius / 2, this.circleRadius];
    let lineLengthArray = [this.circleRadius / 6, this.circleRadius, this.circleRadius * 2];

    let totalLength = this.circleRadius * 4;
    this.radius = totalLength / 2;

    setLineStyle(ctx, this);

    this.createAndDrawEllipses(ctx, circleRadiusArray);
    this.createAndDrawLines(ctx, lineLengthArray);

    if (this.isActive){
      this.drawRect(ctx, this.centerX, this.centerY, this.radius, this.radius, totalLength);
      setBoxStyle(ctx);
      this.drawInteractionBoxes(ctx);
    }
    
  }

  drawEllipse(ctx, centerX, centerY, radiusX, radiusY) {
    ctx.beginPath();
    drawEllipse(ctx, centerX, centerY, radiusX, radiusY);
    ctx.closePath();
    ctx.stroke();
  }

  drawSector(ctx, start, end) {
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.closePath();
    ctx.stroke();
  }

  drawRect(ctx, centerX, centerY, radiusX, radiusY, totalLength) {
    ctx.beginPath();
    ctx.strokeRect(centerX - radiusX, 
      centerY - radiusY,
      totalLength, totalLength);
    ctx.closePath();
    ctx.stroke();
  }

  drawInteractionBoxes(ctx) { 
    drawBox(ctx, this.topLeft.x, this.topLeft.y, this.transform.zoom);
    drawBox(ctx, this.topRight.x, this.topRight.y, this.transform.zoom);
    drawBox(ctx, this.bottomLeft.x, this.bottomLeft.y, this.transform.zoom);
    drawBox(ctx, this.bottomRight.x, this.bottomRight.y, this.transform.zoom);
    
    drawBox(ctx, this.innerTopLeft.x, this.innerTopLeft.y, this.transform.zoom);
    drawBox(ctx, this.innerTopRight.x, this.innerTopRight.y, this.transform.zoom);
    drawBox(ctx, this.innerBottomLeft.x, this.innerBottomLeft.y, this.transform.zoom);
    drawBox(ctx, this.innerBottomRight.x, this.innerBottomRight.y, this.transform.zoom);
  }

  createAndDrawEllipses(ctx, radiusArray) {
    for (let i = 0; i < radiusArray.length; i++) {
      this.circles[i] = new Ellipse(this.centerX, this.centerY, this.color, radiusArray[i]);
      this.drawEllipse(ctx, this.centerX, this.centerY, radiusArray[i], radiusArray[i]);
    }
  }

  createAndDrawLines(ctx, locationArray) {
    this.lines.forEach(e => {
      if (e.direction.length > 1)
        e.inner = getArcPosition(this.centerX, this.centerY, locationArray[0], locationArray[0], e.angle);
      else
        e.inner = getArcPosition(this.centerX, this.centerY, locationArray[1], locationArray[1], e.angle);
      e.outer = getArcPosition(this.centerX, this.centerY, locationArray[2], locationArray[2], e.angle);
      this.drawSector(ctx, e.inner, e.outer);
    })
  }

  isInBounds(x, y) {
      return this.isOnLines(x, y) || this.isOnCircles(x, y) ||
        this.isInBoundsResizeCorner(x, y);
  }

  isInBoundsResizeCorner(x, y) {
    return this.isInBoundsCorner(x, y, this.topLeft) 
      || this.isInBoundsCorner(x, y, this.topRight)
      || this.isInBoundsCorner(x, y, this.bottomRight)
      || this.isInBoundsCorner(x, y, this.bottomLeft);
  }

  isInBoundsCorner(x, y, { x: cx, y: cy }) {
    return isInBoundsBox(x, y, cx, cy, this.transform.zoom);
  }

  isBetween(value, bound1, bound2) {
    const min = Math.min(bound1, bound2);
    const max = Math.max(bound1, bound2);
    return value >= min && value <= max;
  }

  isOnLines(x, y) 
  {
    let retVal = false;
    this.lines.forEach(e => 
      {
        if (isInBoundsLine(x, y, e.inner.x, e.inner.y, e.outer.x, e.outer.y))
          retVal = true;
      });
    return retVal
  }
  
  isOnCircles(x, y) {
    return this.isOnCircle(x, y, this.circleRadius / 6) || 
      this.isOnCircle(x, y, this.circleRadius / 2) || 
      this.isOnCircle(x, y, this.circleRadius);
  }

  isOnCircle(x, y, radius){
    let dx = x - this.centerX;
    let dy = y - this.centerY;
    let angle = Math.atan2(dy, dx);
    let pos = getArcPosition(this.centerX, this.centerY, radius, radius, angle);
    let diffX = Math.abs(x - pos.x);
    let diffY = Math.abs(y - pos.y);
    return diffX <= boxSize && diffY <= boxSize;
  }

  getHoveredBox(x, y) {
    const boxes = this.getBoxes();
    for (let i = 0; i < boxes.length; i++) {
      const box = boxes[i];
      if (isInBoundsBox(x, y, box.x, box.y, this.transform.zoom)) return box;
    }
    return null;
  }

  updateSize(x, y) {
    let largerX = Math.abs(x) > Math.abs(y);
    let max = Math.abs(x) > Math.abs(y) ? Math.abs(x) : Math.abs(y);
    let scaledMax = this.activeBox.location == "O" ? max / 2 : max * 1.3;
    if (largerX) {
      switch (this.activeBox.side) {
        case "TR":
          if (x < 0) this.increaseSize(scaledMax);
          else  this.decreaseSize(scaledMax);
          break;
        case "TL":
          if (x > 0) this.increaseSize(scaledMax);
          else this.decreaseSize(scaledMax);
          break;
        case "BR":
          if (x < 0) this.increaseSize(scaledMax);
          else this.decreaseSize(scaledMax);
          break;
        case "BL":
          if (x > 0) this.increaseSize(scaledMax);
          else this.decreaseSize(scaledMax);
          break;  
      }
    }
    else {    
      switch (this.activeBox.side) {
        case "TR":
          if (y > 0) this.increaseSize(scaledMax);
          else this.decreaseSize(scaledMax);
          break;
        case "TL":
          if (y > 0) this.increaseSize(scaledMax);
          else this.decreaseSize(scaledMax);
          break;
        case "BR":
          if (y < 0) this.increaseSize(scaledMax);
          else this.decreaseSize(scaledMax);
          break;
        case "BL":
          if (y < 0) this.increaseSize(scaledMax);
          else this.decreaseSize(scaledMax);
          break;  
      }
    }
  }

  increaseSize(max) {
    this.circleRadius = this.circleSnapshop + max;
  }

  decreaseSize(max) {
    let canDecrease = this.circleSnapshop - max > 0;
    if (canDecrease) {
      this.circleRadius = this.circleSnapshop - max;
    }
  }

  mousedown(x, y) {
    super.mousedown(x, y);
    let box = this.getHoveredBox(x, y);
    this.activeBox = box;
    if (this.activeBox) {
      this.initialMousePos = {x: x, y: y};
      this.circleSnapshop = this.circleRadius;
    }
    if (this.isInBounds) 
      this.isActive = true;
    this.signalReady();
  }

  mousemove(x, y) {
    const { dx, dy } = this.getAndUpdateInteraction(x, y);
    if (this.activeBox) {
      let difX = this.initialMousePos.x - x; 
      let difY = this.initialMousePos.y - y;
      this.updateSize(difX, difY);
    }
    else {
      this.centerX += dx;
      this.centerY += dy;
    }   
    this.signalReady();
  }

  deactivate() {
    this.isActive = false;
    this.initialMousePos = null;
    this.sizeSnapshop = null;
    this.signalReady();
  }
}